import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, FileText } from 'lucide-react';
import { apiGet } from '../lib/api';
import { useMutate } from '../lib/pageData';
import { useAuth } from '../state/AuthContext';
import { useT } from '../i18n';
import { Modal } from './ui';
import { formatDate } from '../lib/format';
import { LEGAL_VERSIONS } from '../lib/legalVersions';

// DPDP 2026-10-29 (server/routes-dpdp.mjs). Two things a signed-in user may be
// asked once, on whatever screen they land on after sign-in:
//
//  1. THE AGE QUESTION (G12) — only for an account that predates the signup
//     checkbox (`session.ageConfirmation === null`). Answering is stored; NOT
//     answering just asks again next time; neither answer locks anybody out.
//  2. UNREAD NOTICES (G8 breach, G2 privacy) — one at a time, blocking but
//     dismissible. Putting a notice on screen is recorded (stage SEEN) and
//     "I have read this" records the acknowledgement (stage ACK) — the Rule
//     7(1) delivery evidence. "Later" closes it for this visit only. Every
//     notice stays readable in Data & Privacy.
//
// The notice TEXT is written by Sansara staff at the time and is shown exactly
// as written, in the language it was written in; only the chrome here is
// translated (the "shown as written" line says so).

export interface UserNotice {
  id: string;
  // ACCOUNT = a one-person message the SERVER writes (under-18 guardian links,
  // server/minor-accounts.mjs). Its text is a fixed English sentence, so it IS
  // translated; a staff-written notice is shown exactly as written.
  kind: 'BREACH' | 'PRIVACY_NOTICE' | 'ACCOUNT';
  title: string;
  body: string | null;
  breach: { description: string; consequences: string; mitigation: string; userSteps: string } | null;
  respondentContact: string | null;
  policy: { doc: string; version: string } | null;
  createdAt: string;
  seenAt: string | null;
  acknowledgedAt: string | null;
}

/** The body of one notice — shared by the modal and the Data & Privacy list. */
export function NoticeContent({ notice }: { notice: UserNotice }) {
  const t = useT();
  const para = 'whitespace-pre-line text-sm leading-relaxed text-ink dark:text-slate-200';
  const head = 'text-xs font-semibold uppercase tracking-wide text-ink-faint dark:text-slate-400';
  return (
    <div className="space-y-3">
      {notice.breach ? (
        <>
          <div><p className={head}>{t('What happened')}</p><p className={para}>{notice.breach.description}</p></div>
          <div><p className={head}>{t('What this could mean for you')}</p><p className={para}>{notice.breach.consequences}</p></div>
          <div><p className={head}>{t('What Sansara has done and will do')}</p><p className={para}>{notice.breach.mitigation}</p></div>
          <div><p className={head}>{t('What you can do')}</p><p className={para}>{notice.breach.userSteps}</p></div>
        </>
      ) : null}
      {notice.body ? <p className={para}>{notice.kind === 'ACCOUNT' ? t(notice.body) : notice.body}</p> : null}
      {notice.respondentContact ? (
        <div><p className={head}>{t('Who can answer your questions')}</p><p className={para}>{notice.respondentContact}</p></div>
      ) : null}
      {notice.policy?.doc === 'privacy' ? (
        <p className="text-sm"><Link className="font-semibold text-brand-700 underline dark:text-brand-400" to="/privacy">{t('Read the full Privacy Policy')}</Link></p>
      ) : notice.policy?.doc === 'terms' ? (
        <p className="text-sm"><Link className="font-semibold text-brand-700 underline dark:text-brand-400" to="/terms">{t('Read the full Terms')}</Link></p>
      ) : null}
      {notice.kind === 'ACCOUNT' ? null : <p className="text-xs text-ink-faint dark:text-slate-400">{t('This notice is shown exactly as Sansara wrote it, in the language it was written in.')}</p>}
    </div>
  );
}

// Static t() calls on both branches so check:i18n-coverage can see both labels.
export const useNoticeKindLabel = () => {
  const t = useT();
  return (kind: UserNotice['kind']) => (kind === 'BREACH' ? t('Data breach notice') : kind === 'ACCOUNT' ? t('Account notice') : t('Privacy notice'));
};

/** A notice title: a server-written ACCOUNT notice is translated, a staff one is shown as written. */
export const useNoticeTitle = () => {
  const t = useT();
  return (n: Pick<UserNotice, 'kind' | 'title'>) => (n.kind === 'ACCOUNT' ? t(n.title) : n.title);
};

export default function AccountNotices() {
  const t = useT();
  const { session, refreshSession } = useAuth();
  const mutate = useMutate();
  const [notices, setNotices] = useState<UserNotice[]>([]);
  const [later, setLater] = useState<Set<string>>(() => new Set());
  const [ageClosed, setAgeClosed] = useState(false);
  const [ageResult, setAgeResult] = useState<'UNDER_18' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seenSent = useRef<Set<string>>(new Set());
  const kindLabel = useNoticeKindLabel();
  const noticeTitle = useNoticeTitle();
  // Under-18 accounts (server/minor-accounts.mjs): a FROZEN account is told why
  // and where its data is; a TURNED_18 account confirms its age and accepts the
  // current documents before anything else opens (the server refuses until then).
  const minor = session?.minor ?? null;
  const [frozenClosed, setFrozenClosed] = useState(false);
  const [adultTicked, setAdultTicked] = useState(false);
  const [handOverClosed, setHandOverClosed] = useState(false);

  const load = useCallback(() => {
    apiGet<{ notices: UserNotice[] }>('/api/notices')
      .then((d) => setNotices(d.notices ?? []))
      .catch(() => { /* a notice that cannot load is retried on the next open */ });
  }, []);
  useEffect(() => { if (session?.twoFactorEnrolled) load(); }, [session?.id, session?.twoFactorEnrolled, load]);

  const askAge = Boolean(session && session.twoFactorEnrolled && session.ageConfirmation == null && !ageClosed);
  const handOver = Boolean(session?.twoFactorEnrolled && minor?.status === 'TURNED_18' && !handOverClosed);
  const showFrozen = Boolean(session?.twoFactorEnrolled && minor?.status === 'FROZEN' && !frozenClosed);
  const current = askAge || ageResult || handOver || showFrozen ? null : notices.find((n) => !n.acknowledgedAt && !later.has(n.id)) ?? null;

  // Record that the notice was put on screen — once per notice.
  useEffect(() => {
    if (!current || current.seenAt || seenSent.current.has(current.id)) return;
    seenSent.current.add(current.id);
    mutate<{ notice: UserNotice }>('/api/notices/ack', { noticeId: current.id, stage: 'SEEN' })
      .then((d) => setNotices((all) => all.map((n) => (n.id === d.notice.id ? d.notice : n))))
      .catch(() => { seenSent.current.delete(current.id); });
  }, [current, mutate]);

  const acknowledge = async () => {
    if (!current) return;
    setBusy(true);
    setError(null);
    try {
      const d = await mutate<{ notice: UserNotice }>('/api/notices/ack', { noticeId: current.id, stage: 'ACK' });
      setNotices((all) => all.map((n) => (n.id === d.notice.id ? d.notice : n)));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not save. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const answerAge = async (isAdult: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await mutate('/api/account/confirm-age', { isAdult });
      if (!isAdult) setAgeResult('UNDER_18');
      setAgeClosed(true);
      await refreshSession();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not save. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const confirmAdult = async () => {
    setBusy(true);
    setError(null);
    try {
      await mutate('/api/account/adult-transition', { isAdult: true, policyVersions: LEGAL_VERSIONS });
      await refreshSession();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not save. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (handOver) {
    // Closable for this visit (like the age question); until the holder
    // confirms, the server keeps every under-18 restriction and refuses the
    // rest of the app with a sentence saying why, and this asks again next visit.
    return (
      <Modal open onClose={() => setHandOverClosed(true)} title={t('You have turned 18')}>
        <p className="text-sm leading-relaxed text-ink-soft dark:text-slate-300">
          {t('Your account is now your own and your guardian can no longer see it. Confirm your age and accept the current Privacy Policy and Terms to carry on.')}
        </p>
        <p className="mt-3 text-sm">
          <Link className="font-semibold text-brand-700 underline dark:text-brand-400" to="/privacy">{t('Read the full Privacy Policy')}</Link>
          {' · '}
          <Link className="font-semibold text-brand-700 underline dark:text-brand-400" to="/terms">{t('Read the full Terms')}</Link>
        </p>
        <label className="mt-4 flex items-start gap-2.5 text-sm text-ink dark:text-slate-200" htmlFor="adultTransition">
          <input id="adultTransition" type="checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" checked={adultTicked} onChange={(e) => setAdultTicked(e.target.checked)} />
          <span>{t('I am 18 or older, and I accept the current Privacy Policy and Terms.')}</span>
        </label>
        {error ? <p className="mt-3 text-sm font-medium text-loss dark:text-loss-dark" role="alert">{t(error)}</p> : null}
        <button type="button" className="btn-primary mt-5 w-full" disabled={busy || !adultTicked} onClick={confirmAdult}>{t('Confirm')}</button>
      </Modal>
    );
  }

  if (showFrozen && minor) {
    return (
      <Modal open onClose={() => setFrozenClosed(true)} title={t('Your guardian link has ended')}>
        <p className="text-sm leading-relaxed text-ink-soft dark:text-slate-300">
          {t('Your account is frozen. You can download your data in Data & Privacy. The account and its data will be erased 30 days after the link ended.')}
        </p>
        {minor.eraseAfter ? (
          <p className="mt-2 text-sm font-semibold text-ink dark:text-slate-200">{t('Erased on {date}', { date: formatDate(minor.eraseAfter) })}</p>
        ) : null}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <Link className="btn-primary flex-1 text-center" to="/app/settings" onClick={() => setFrozenClosed(true)}>{t('Download my data')}</Link>
          <button type="button" className="btn-secondary flex-1" onClick={() => setFrozenClosed(true)}>{t('OK')}</button>
        </div>
      </Modal>
    );
  }

  if (askAge) {
    return (
      <Modal open onClose={() => setAgeClosed(true)} title={t('Please confirm your age')}>
        <p className="text-sm leading-relaxed text-ink-soft dark:text-slate-300">{t('Sansara is for adults. Are you 18 or older?')}</p>
        {error ? <p className="mt-3 text-sm font-medium text-loss dark:text-loss-dark" role="alert">{t(error)}</p> : null}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button type="button" className="btn-primary flex-1" disabled={busy} onClick={() => answerAge(true)}>{t('Yes, I am 18 or older')}</button>
          <button type="button" className="btn-secondary flex-1" disabled={busy} onClick={() => answerAge(false)}>{t('No, I am under 18')}</button>
        </div>
      </Modal>
    );
  }

  if (ageResult) {
    return (
      <Modal open onClose={() => setAgeResult(null)} title={t('Thank you for telling us')}>
        <p className="text-sm leading-relaxed text-ink-soft dark:text-slate-300">
          {t('Sansara is meant for adults only. Write to grievance@sansarafin.com and we will help you close your account and delete your data.')}
        </p>
        <button type="button" className="btn-primary mt-5 w-full" onClick={() => setAgeResult(null)}>{t('OK')}</button>
      </Modal>
    );
  }

  if (!current) return null;
  const Icon = current.kind === 'BREACH' ? ShieldAlert : FileText;
  return (
    <Modal open wide onClose={() => setLater((s) => new Set(s).add(current.id))} title={noticeTitle(current)}>
      <p className="-mt-2 mb-3 flex items-center gap-1.5 text-xs font-semibold text-brand-700 dark:text-brand-400">
        <Icon size={14} /> {kindLabel(current.kind)} · {formatDate(current.createdAt)}
      </p>
      <NoticeContent notice={current} />
      {error ? <p className="mt-3 text-sm font-medium text-loss dark:text-loss-dark" role="alert">{t(error)}</p> : null}
      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <button type="button" className="btn-primary flex-1" disabled={busy} onClick={acknowledge}>{t('I have read this')}</button>
        <button type="button" className="btn-secondary flex-1" disabled={busy} onClick={() => setLater((s) => new Set(s).add(current.id))}>{t('Remind me later')}</button>
      </div>
      <p className="mt-3 text-xs text-ink-faint dark:text-slate-400">{t('You can read every notice again in Data & Privacy.')}</p>
    </Modal>
  );
}
